import { useState, useEffect } from "react";
import { useReactiveVar } from "@apollo/client";
import {
  useGetCurrentUserCanEditPaymentPrefForPositionsQuery,
  useGetConsolidatedPaymentPrefForPositionQuery,
} from "../../../../../graphql/supergraph.index";
import { CreatePaymentPrefEntryPoint, WorkflowMode } from "../../../common/enums";
import { type Position } from "../../../common/types";
import { type CreateWorkflowParams } from "../../../Workflows/PaymentPrefForPositionWorkflow/types";
import { positionPaymentPrefVar } from "../apollo-vars";
import { type UsePositionPaymentPrefParams, type UsePositionPaymentPrefReturn } from "./types";
export const usePositionPaymentPref = ({
  accountId,
  entityId,
  entityName,
  positionId,
  paymentPrefId,
}: UsePositionPaymentPrefParams): UsePositionPaymentPrefReturn => {
  // State Management
  const [showWorkflowComponent, setShowWorkflowComponent] = useState<boolean>(false);
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode | null>(null);
  const [workflowParams, setWorkflowParams] =
    useState<UsePositionPaymentPrefReturn["workflowParams"]>(null);

  /**
   * Reactive Variable for *Position* Payment Preference ID This component hierarchy will react to changes of this prop
   */
  useReactiveVar(positionPaymentPrefVar);

  /**
   * Update the apollo reactive var with the prop-drilled payment preference from container component;
   * We do not want to use this payment pref if it gets deleted etc. so on rerenders we don't refer to it.
   */
  useEffect(() => {
    positionPaymentPrefVar(paymentPrefId ?? null);
  }, [paymentPrefId]);

  // Fetch User Permissions
  const {
    data: currentUserData,
    loading: currentUserLoading,
    error: currentUserError,
  } = useGetCurrentUserCanEditPaymentPrefForPositionsQuery({
    variables: {
      accountId,
    },
    fetchPolicy: "cache-and-network",
  });

  // Fetch Payment Preference Data
  const {
    data: paymentPrefData,
    loading: paymentPrefLoading,
    error: paymentPrefError,
  } = useGetConsolidatedPaymentPrefForPositionQuery({
    variables: {
      paymentPrefIds: positionPaymentPrefVar() ? [positionPaymentPrefVar()!] : [],
    },
    skip: !positionPaymentPrefVar(),
    fetchPolicy: "cache-and-network",
  });

  // Handle Workflow Callbacks
  const handlePaymentPrefCallbacks = ({
    workflowMode,
    createEntryPoint,
  }: {
    workflowMode: WorkflowMode;
    createEntryPoint?: CreatePaymentPrefEntryPoint;
  }) => {
    setWorkflowMode(workflowMode);
    setShowWorkflowComponent(true);

    // TODO refactor position and flatten it inline with `PositionPaymentPrefProps` https://junipersquare.atlassian.net/browse/FF-3202
    const position = {
      id: positionId,
      accountId,
      investmentEntityId: entityId,
      investmentEntityName: entityName,
    } as unknown as Position;

    if (workflowMode === WorkflowMode.Edit && paymentPrefData?.paymentPrefs?.[0]) {
      setWorkflowParams({
        position,
        existingPaymentPref: paymentPrefData?.paymentPrefs?.[0],
      });
    } else if (workflowMode === WorkflowMode.Create) {
      const createWorkflowParams: CreateWorkflowParams = {
        position,
        ...(createEntryPoint &&
          createEntryPoint === CreatePaymentPrefEntryPoint.Switch && {
            sourcePaymentPrefId: paymentPrefData?.paymentPrefs?.[0].id,
          }),
      };
      setWorkflowParams(createWorkflowParams);
    } else {
      throw new Error("Bad state");
    }
  };

  // Determine Permissions
  const canEdit =
    currentUserData?.currentUser?.canEditPaymentPrefsForAccountPermission || false;
  const canReveal = currentUserData?.currentUser?.canRevealPaymentPrefPermission || false;

  // Combine Loading and Error States
  const loading = currentUserLoading || paymentPrefLoading;
  const error = Boolean(currentUserError || paymentPrefError);

  // Extract Payment Preference
  const paymentPref = paymentPrefData?.paymentPrefs?.[0];

  return {
    showWorkflowComponent,
    workflowMode,
    workflowParams,
    paymentPref: paymentPref ?? null,
    paymentPrefId: paymentPref?.id,
    canEdit,
    canReveal,
    loading,
    error,
    setShowWorkflowComponent,
    handlePaymentPrefCallbacks,
  };
};
